import React, { forwardRef, ReactNode, Ref, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Collapse, Stack, useTheme } from '@mui/material';
import _pick from 'lodash/pick';

import ResetIcon from '@/assets/icons/reset.svg?react';
import { SORTING_AND_PAGINATION_KEYS } from '@/hooks/useEntityList';
import typography from '@/theme/typography';
import { formStateFallback } from '@/utils/formStateFallback';
import BookmarkIcon from '@/views/Filters/assets/bookmark.svg?react';

interface FilterPanelProps {
  children: ReactNode;
  isOpen?: boolean;
  onSave?: () => void;
}

const FilterPanel = forwardRef(({ children, isOpen, onSave }: FilterPanelProps, ref: Ref<HTMLDivElement>) => {
  const { spacing, palette } = useTheme();
  const { t } = useTranslation('filters');

  const { reset, getValues, formState } = useFormContext();

  const handleReset = useCallback(() => {
    const paginationAndSorting = _pick(getValues(), SORTING_AND_PAGINATION_KEYS);
    const fallbackValue = formStateFallback(getValues(), {
      ...formState.defaultValues,
      ...paginationAndSorting
    });
    reset(fallbackValue);
  }, [reset, formState, getValues]);

  return (
    <Collapse className="FiltersPanel-root" in={isOpen}>
      <Stack
        ref={ref}
        direction="row"
        height={spacing(7)}
        textAlign="center"
        justifyContent="space-between"
        sx={{
          background: palette.primary[100],
          borderRadius: spacing(0.5),
          margin: spacing(2, 0, 0),
          p: spacing(0, 2),
          '& .MuiButtonBase-root': {
            display: 'flex',
            alignItems: 'center',
            ...typography.body3,
            fontWeight: typography.fontWeightMedium
          },
          '& .MuiButton-icon': {
            width: spacing(2)
          }
        }}
      >
        <Stack direction="row" alignItems="center">
          {children}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            '& .MuiButtonBase-root': {
              p: spacing(0, 1)
            }
          }}
        >
          <Button
            startIcon={<ResetIcon />}
            sx={{
              color: 'primary.400',
              whiteSpace: 'nowrap',
              '&:hover': {
                bgcolor: 'grey.A100'
              }
            }}
            onClick={handleReset}
          >
            {t('filters.resetAll')}
          </Button>
          {onSave && (
            <Button
              startIcon={<BookmarkIcon />}
              sx={{
                '&:hover': {
                  bgcolor: 'grey.A100'
                }
              }}
              onClick={onSave}
            >
              {t('filters.save')}
            </Button>
          )}
        </Stack>
      </Stack>
    </Collapse>
  );
});

FilterPanel.displayName = 'FilterPanel';

export default FilterPanel;
